/**
 * Typed wrapper over `window.Telegram.WebApp`.
 *
 * Two rules, both learned from the SDK source rather than the documentation:
 *
 * 1. **In a plain browser the object still exists and every method succeeds
 *    while doing nothing.** `postEvent` falls through to
 *    `callback({notAvailable: true})` when there is no host — nothing throws,
 *    nothing reports. So "is the SDK there" is never the question; whether we
 *    were launched as a Mini App is answered by `launch-params.ts`.
 *
 * 2. **Capabilities landed across Bot API 6.1 → 9.6 and members run whatever
 *    client they run**, so every call here is version-gated. The gate is not
 *    cosmetic: the SDK splits into two failure modes and they need different
 *    treatment.
 *
 *      - *warn and no-op*: BackButton, HapticFeedback, setBackgroundColor,
 *        setBottomBarColor, disableVerticalSwipes. Safe to call blind; gated
 *        anyway so an old client does not fill its console.
 *      - *THROW*: `setHeaderColor` with a `#RRGGBB` string on **6.1–6.8**
 *        raises `WebAppHeaderColorKeyInvalid`. Before 6.9 it accepts only the
 *        `bg_color` / `secondary_bg_color` keywords. This is the one that
 *        white-screens an old client, and it is why `setHeaderColor` is gated
 *        at **6.9** while `setBackgroundColor` is gated at 6.1 — the two look
 *        symmetrical and are not.
 *
 * Anything not needed is deliberately absent. This is a wrapper, not a
 * re-declaration of the whole SDK.
 */

import { getClientVersion, isMiniApp } from './launch-params.ts';

type HapticStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
type HapticNotification = 'error' | 'success' | 'warning';

interface TelegramWebApp {
  readonly version: string;
  readonly platform: string;
  readonly colorScheme: 'light' | 'dark';
  readonly initData: string;
  isVersionAtLeast(version: string): boolean;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor(color: string): void;
  disableVerticalSwipes(): void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  BackButton: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void;
    offClick(handler: () => void): void;
  };
  HapticFeedback: {
    impactOccurred(style: HapticStyle): void;
    notificationOccurred(type: HapticNotification): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/**
 * The live SDK object, or null.
 *
 * Exported for `haptics.ts`, which is the only other module that talks to the
 * SDK directly. Everything else goes through the wrappers below.
 */
export function webAppSdk(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  if (!isMiniApp()) return null;
  return window.Telegram?.WebApp ?? null;
}

function sdk(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

/**
 * Version gate.
 *
 * Prefers the SDK's own comparator — it is component-wise and numeric, so
 * `'6.10' >= '6.9'` is correctly true, which neither string nor float
 * comparison gets right. Falls back to the version from the launch hash when
 * the SDK script did not load, so a blocked third-party script degrades to
 * "assume the oldest client" rather than "assume the newest".
 */
export function isVersionAtLeast(version: string): boolean {
  const app = sdk();
  if (app) return app.isVersionAtLeast(version);
  return compareVersions(getClientVersion(), version) >= 0;
}

/** The SDK's `versionCompare`, reimplemented for the no-SDK path. */
export function compareVersions(a: string, b: string): number {
  const left = a.trim().split('.');
  const right = b.trim().split('.');
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const p1 = Number.parseInt(left[i] ?? '', 10) || 0;
    const p2 = Number.parseInt(right[i] ?? '', 10) || 0;
    if (p1 === p2) continue;
    return p1 > p2 ? 1 : -1;
  }
  return 0;
}

/**
 * Every call goes through here.
 *
 * The try/catch is not defensive padding: `setHeaderColor`,
 * `setBackgroundColor` and `setBottomBarColor` throw on a colour they cannot
 * parse *regardless of version* — and they reject `oklch()`, which is what
 * HeroUI's own default theme is written in. A colour that fails to apply is a
 * cosmetic problem; an uncaught throw during startup is a white screen.
 */
export function callSafely(what: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    console.warn(`[telegram] ${what} failed`, error);
  }
}

const attempt = callSafely;

/**
 * Colours handed to Telegram's chrome so it matches the app rather than the
 * user's Telegram theme.
 *
 * Literal hex, duplicated from `styles.css`, for two reasons: Telegram's
 * `parseColorToHex` accepts only `#rgb`, `#rrggbb`, `rgb()` and `rgba()` and
 * THROWS on anything else — including the `oklch()` that a computed style
 * could return — and reading a custom property at startup races the
 * stylesheet. Two values are cheaper to keep in step than a runtime failure.
 */
const CHROME = {
  light: '#FFFCF7',
  dark: '#0C0908',
} as const;

/**
 * Announce readiness, take the full height, and paint Telegram's chrome.
 *
 * Called once, after the first paint. `ready()` is what tells the client to
 * stop showing its loading placeholder; calling it before the app can render
 * shows the user an empty app instead of a spinner.
 */
export function initWebApp(scheme: 'light' | 'dark'): void {
  const app = sdk();
  if (!app || !isMiniApp()) return;

  attempt('ready', () => app.ready());
  attempt('expand', () => app.expand());

  // 7.7+. Without it a vertical swipe that starts inside a scrolling table is
  // taken by Telegram as "close the app" — which reads as a crash mid-scroll,
  // and this app is mostly tables.
  if (isVersionAtLeast('7.7')) {
    attempt('disableVerticalSwipes', () => app.disableVerticalSwipes());
  }

  applyChromeColors(scheme);
}

/** Repaint Telegram's own chrome after a theme change. */
export function applyChromeColors(scheme: 'light' | 'dark'): void {
  const app = sdk();
  if (!app || !isMiniApp()) return;

  const color = CHROME[scheme];

  // 6.9, NOT 6.1 — see the file docblock. On 6.1–6.8 a hex here throws.
  if (isVersionAtLeast('6.9')) {
    attempt('setHeaderColor', () => app.setHeaderColor(color));
  }
  if (isVersionAtLeast('6.1')) {
    attempt('setBackgroundColor', () => app.setBackgroundColor(color));
  }
  // 7.10+. This is also the Android navigation bar, which is why it matters
  // more than it sounds: the bottom tab bar sits directly against it.
  if (isVersionAtLeast('7.10')) {
    attempt('setBottomBarColor', () => app.setBottomBarColor(color));
  }
}

/** Telegram's own light/dark signal. Only the SIGNAL is taken; the palette is ours. */
export function getColorScheme(): 'light' | 'dark' | null {
  const app = sdk();
  if (!app || !isMiniApp()) return null;
  return app.colorScheme === 'dark' ? 'dark' : 'light';
}

/** Subscribe to Telegram's theme changes. Returns an unsubscribe function. */
export function onThemeChanged(handler: () => void): () => void {
  const app = sdk();
  if (!app || !isMiniApp()) return () => undefined;
  app.onEvent('themeChanged', handler);
  return () => app.offEvent('themeChanged', handler);
}

/**
 * Bind Telegram's BackButton to a handler. Returns an unbind function.
 *
 * Without this, the hardware back gesture on a sub-page closes the entire Mini
 * App, which is indistinguishable from a crash. 6.1+; below that the SDK warns
 * and no-ops, so the gate only keeps the console quiet.
 */
export function showBackButton(onBack: () => void): () => void {
  const app = sdk();
  if (!app || !isMiniApp() || !isVersionAtLeast('6.1')) return () => undefined;

  attempt('BackButton.onClick', () => app.BackButton.onClick(onBack));
  attempt('BackButton.show', () => app.BackButton.show());

  return () => {
    attempt('BackButton.hide', () => app.BackButton.hide());
    attempt('BackButton.offClick', () => app.BackButton.offClick(onBack));
  };
}

export type { HapticStyle, HapticNotification };
